import { cn } from '@/lib/utils';
import { Play, Lock, Check } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from './tooltip';
import { useSignedUrl } from '@/hooks/useSignedUrl';
import { motion, AnimatePresence } from 'framer-motion';

interface ABVersionThumbnailV10Props {
  /** Version label (e.g. "A", "B", "C") */
  label: string;
  /** Raw S3 key — resolved internally via useSignedUrl */
  s3Key?: string;
  /** Fallback: already-resolved thumbnail URL */
  thumbnailUrl?: string;
  /** Whether this version is currently selected */
  isActive: boolean;
  /** Click handler to select this version */
  onClick: () => void;
  /** Whether this slot is locked (upsell) */
  locked?: boolean;
  /** Whether this version is disabled by client (greyed out) */
  disabled?: boolean;
  /** Show a checkbox overlay */
  showCheckbox?: boolean;
  /** Checkbox state */
  checked?: boolean;
  /** Checkbox toggle callback */
  onCheckChange?: (checked: boolean) => void;
}

export function ABVersionThumbnailV10({
  label,
  s3Key,
  thumbnailUrl,
  isActive,
  onClick,
  locked = false,
  disabled = false,
  showCheckbox = false,
  checked = true,
  onCheckChange,
}: ABVersionThumbnailV10Props) {
  // Resolve S3 key to a signed URL; falls back to thumbnailUrl
  const signedUrl = useSignedUrl(s3Key ?? null);
  const videoSrc = signedUrl ?? thumbnailUrl ?? null;

  const isInteractive = !locked && !disabled;

  const content = (
    <div className="flex flex-col items-center gap-1.5">
      <div
        role="button"
        tabIndex={isInteractive ? 0 : -1}
        onClick={isInteractive ? onClick : undefined}
        onKeyDown={isInteractive ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
        className={cn(
          "flex flex-col items-center gap-1.5 group",
          (locked || disabled) && "cursor-default",
          isInteractive && "cursor-pointer"
        )}
      >
        {/* Version label badge */}
        <span
          className={cn(
            "text-[10px] font-semibold uppercase tracking-[0.05em] px-2 py-0.5 rounded-full transition-colors",
            locked || disabled
              ? "bg-[#1f1f1f] text-white/25"
              : isActive
                ? "bg-warning text-[#1a1a00]"
                : "bg-[#1f1f1f] text-white/60 group-hover:text-white/80"
          )}
        >
          Version {label}
        </span>

        {/* Thumbnail container */}
        <div
          className={cn(
            "w-[68px] h-[121px] rounded-xl overflow-hidden transition-all duration-200 relative",
            locked || disabled
              ? "opacity-30"
              : isActive
                ? "scale-105"
                : "opacity-60 group-hover:opacity-80",
            disabled && "grayscale"
          )}
          style={{
            boxShadow: locked || disabled
              ? '0 0 0 1px hsl(var(--v10-border))'
              : isActive
                ? '0 0 0 2px #FFCF0C'
                : '0 0 0 1px hsl(var(--v10-border))',
          }}
        >
          {locked ? (
            <div className="w-full h-full flex items-center justify-center" style={{ background: 'hsl(var(--v10-bg-surface))' }}>
              <Lock className="w-4 h-4 text-white/20" />
            </div>
          ) : videoSrc ? (
            <video
              src={`${videoSrc}#t=0.001`}
              className="w-full h-full object-cover"
              muted
              preload="metadata"
              playsInline
            >
              <track kind="captions" srcLang="fr" label="Français" default />
            </video>
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: 'hsl(var(--v10-bg-surface))' }}>
              <Play className="w-4 h-4 text-white/30" />
            </div>
          )}

          {/* Active overlay */}
          {isActive && !locked && !disabled && (
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ background: 'rgba(255,207,12,0.1)' }}
            >
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(255,207,12,0.9)' }}
              >
                <Play className="w-3 h-3 fill-[#1a1a00] text-[#1a1a00]" />
              </div>
            </div>
          )}

          {/* Checkbox inside thumbnail, top-right */}
          {showCheckbox && !locked && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCheckChange?.(!checked);
              }}
              className={cn(
                "absolute top-1.5 right-1.5 w-4 h-4 rounded border flex items-center justify-center transition-all z-10",
                checked
                  ? "border-transparent"
                  : "border-white/30 bg-black/30"
              )}
              style={checked ? { background: 'hsl(var(--v10-status-success))', borderColor: 'hsl(var(--v10-status-success))' } : {}}
            >
              <AnimatePresence>
                {checked && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  >
                    <Check className="h-2.5 w-2.5 text-white" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (locked) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {content}
        </TooltipTrigger>
        <TooltipContent
          side="left"
          className="max-w-[200px] text-center text-xs"
        >
          Boostez vos résultats avec l'option Multihook lors de votre prochain tournage
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
}
